import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getEnabledApps, type AppConfig } from "./apps.config";

export type SuiteAppsLoader = () => Promise<AppConfig[]>;

const SuiteAppsContext = createContext<AppConfig[] | null>(null);

export function useSuiteAppsList(): AppConfig[] {
  const fromApi = useContext(SuiteAppsContext);
  return fromApi ?? getEnabledApps();
}

export function useSuiteAppConfig(appId?: string): AppConfig | undefined {
  const apps = useSuiteAppsList();
  if (!appId) return undefined;
  return apps.find((app) => app.id === appId);
}

export interface SuiteAppsBootstrapProps {
  children: React.ReactNode;
  /** When set, fetches on mount and when `reloadKey` changes; results override static config. */
  loadApps?: SuiteAppsLoader;
  /** Change to re-fetch (e.g. user id or auth epoch). */
  reloadKey?: string;
}

/**
 * Optional wrapper: loads dynamic app list from People and provides it to sidebar/launcher.
 */
export const SuiteAppsBootstrap: React.FC<SuiteAppsBootstrapProps> = ({
  children,
  loadApps,
  reloadKey = "0",
}) => {
  const [apps, setApps] = useState<AppConfig[] | null>(null);
  // Callers pass inline arrows, so the loader's identity changes on every
  // render. Keyed on it, this refetched on every module switch; only
  // `reloadKey` (and whether there is a loader at all) should.
  const loadAppsRef = useRef(loadApps);
  loadAppsRef.current = loadApps;
  const hasLoader = Boolean(loadApps);

  useEffect(() => {
    const load = loadAppsRef.current;
    if (!load) {
      setApps(null);
      return;
    }
    let cancelled = false;
    void load().then((list) => {
      if (!cancelled) setApps(list);
    });
    return () => {
      cancelled = true;
    };
  }, [hasLoader, reloadKey]);

  const value = useMemo(() => apps, [apps]);

  return <SuiteAppsContext.Provider value={value}>{children}</SuiteAppsContext.Provider>;
};
